import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that allows users to select a product and variant to view third-party test results.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} productSelect - The product select dropdown.
 * @property {HTMLSelectElement} variantSelect - The variant select dropdown.
 * @property {HTMLElement} variantSelectorGroup - The variant selector group container.
 * @property {HTMLElement} resultsContainer - The results container.
 * @property {HTMLElement} productResults - The product results container.
 * @property {HTMLElement} noResultsSection - The no results section.
 * @property {HTMLElement} pdfModal - The PDF modal component.
 * @property {HTMLDialogElement} pdfDialog - The PDF dialog element.
 * @property {HTMLElement} pdfDialogTitle - The PDF dialog title.
 * @property {HTMLElement} pdfDialogClose - The PDF dialog close button.
 * @property {HTMLObjectElement} pdfObject - The PDF object element.
 * @property {HTMLIFrameElement} pdfIframe - The PDF iframe element (fallback).
 * @extends {Component<Refs>}
 */
class ThirdPartyTestsComponent extends Component {
  requiredRefs = [
    'productSelect',
    'variantSelect',
    'variantSelectorGroup',
    'resultsContainer',
    'productResults',
    'noResultsSection',
    'pdfDialog',
    'pdfObject',
    'pdfIframe',
    'pdfDialogTitle',
    'pdfDialogClose',
  ];

  /** @type {Array<{id: string, title: string, handle: string, url: string}>} */
  #products = [];
  /** @type {Array<{id: string, title: string, product_id: string}>} */
  #variants = [];
  /** @type {any} */
  #productJsonData = null;
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#loadProducts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Load all products from the store
   */
  async #loadProducts() {
    this.#controller.abort();
    this.#controller = new AbortController();

    try {
      // Fetch products from collections/all
      const productsUrl = new URL('/collections/all', location.origin);
      const response = await fetch(productsUrl.toString(), {
        signal: this.#controller.signal,
      });

      if (!response.ok) throw new Error('Failed to fetch products');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find all product cards
      const productCards = doc.querySelectorAll('[data-product-id], product-card');
      this.#products = [];

      for (const card of productCards) {
        const productId = card.getAttribute('data-product-id') || card.closest('[data-product-id]')?.getAttribute('data-product-id');
        if (!productId) continue;

        const link = card.querySelector('a[href*="/products/"]');
        if (!link || !(link instanceof HTMLAnchorElement)) continue;

        const productUrl = new URL(link.href, location.origin).toString();
        const handle = productUrl.split('/products/')[1]?.split('?')[0]?.split('#')[0] || '';

        // Try to get product name
        const titleElement = card.querySelector('product-title, h2, h3, .product-card__title, [class*="title"]');
        const productName = titleElement?.textContent?.trim() || 'Product';

        // Avoid duplicates
        if (!this.#products.find((p) => p.id === productId)) {
          this.#products.push({
            id: productId,
            title: productName,
            handle: handle,
            url: productUrl,
          });
        }
      }

      // Sort products alphabetically
      this.#products.sort((a, b) => a.title.localeCompare(b.title));

      this.#populateProductSelect();

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading products:', error);
    }
  }

  /**
   * Populate the product select dropdown
   */
  #populateProductSelect() {
    const select = this.refs.productSelect;
    select.innerHTML = '<option value="">-- Select a product --</option>';

    for (const product of this.#products) {
      const option = document.createElement('option');
      option.value = product.id;
      option.textContent = product.title;
      option.dataset.handle = product.handle;
      option.dataset.url = product.url;
      select.appendChild(option);
    }
  }

  /**
   * Handle product selection change
   * @param {Event} event
   */
  handleProductChange = async (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const productId = select.value;

    if (!productId) {
      this.refs.variantSelectorGroup.hidden = true;
      this.refs.resultsContainer.hidden = true;
      return;
    }

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;
    const productHandle = selectedOption.dataset.handle || '';
    const productUrl = selectedOption.dataset.url || '';

    this.refs.variantSelectorGroup.hidden = true;
    this.refs.resultsContainer.hidden = true;

    this.#controller.abort();
    this.#controller = new AbortController();

    try {
      this.#variants = [];
      let productJsonData = null;

      // Method 1: Try to fetch product JSON data (most reliable - includes all variants)
      try {
        const jsonUrl = `/products/${productHandle}.json`;
        const jsonResponse = await fetch(jsonUrl, {
          signal: this.#controller.signal,
        });

        if (jsonResponse.ok) {
          const productData = await jsonResponse.json();
          productJsonData = productData.product; // Store for later use
          
          if (productJsonData && productJsonData.variants) {
            for (const variant of productJsonData.variants) {
              if (variant.id && variant.title) {
                this.#variants.push({
                  id: variant.id.toString(),
                  title: variant.title,
                  product_id: productId,
                });
              }
            }
            
            if (this.#variants.length > 0) {
              this.#populateVariantSelect();
              this.refs.variantSelectorGroup.hidden = false;
              
              // Store product JSON data for later use when fetching test data
              this.#productJsonData = productJsonData;
              return;
            }
          }
        }
      } catch (e) {
        // Fall back to HTML parsing
        console.log('JSON endpoint not available, falling back to HTML parsing');
      }

      // Method 2: Fetch product page HTML and parse variants
      const response = await fetch(productUrl || `/products/${productHandle}`, {
        signal: this.#controller.signal,
      });

      if (!response.ok) throw new Error('Failed to fetch product');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Method 1: Look for variant options in variant picker selects (most reliable)
      const variantSelects = doc.querySelectorAll('variant-picker select, .variant-option__select, select[name*="option"]');
      for (const select of variantSelects) {
        const options = select.querySelectorAll('option[data-variant-id]');
        for (const option of options) {
          const variantId = option.getAttribute('data-variant-id');
          const optionElement = /** @type {HTMLOptionElement} */ (option);
          let variantTitle = optionElement.textContent?.trim() || optionElement.value || '';
          
          // Skip placeholder/default options
          if (!variantId || variantId === 'null' || variantId === '' || variantTitle === '' || variantTitle === '-- Select a variant --') {
            continue;
          }
          
          // Clean up title (remove " - Unavailable" suffix if present)
          variantTitle = variantTitle.replace(/\s*-\s*Unavailable$/i, '').trim();

          if (variantId && variantTitle) {
            // Avoid duplicates
            if (!this.#variants.find((v) => v.id === variantId)) {
              this.#variants.push({
                id: variantId,
                title: variantTitle,
                product_id: productId,
              });
            }
          }
        }
      }
      
      // Also check for variant buttons/swatches with data-variant-id
      const variantButtons = doc.querySelectorAll('[data-variant-id]:not(option)');
      for (const button of variantButtons) {
        const variantId = button.getAttribute('data-variant-id');
        if (variantId && variantId !== 'null' && variantId !== '') {
          const variantTitle = button.textContent?.trim() || button.getAttribute('aria-label') || '';
          if (variantTitle && !this.#variants.find((v) => v.id === variantId)) {
            this.#variants.push({
              id: variantId,
              title: variantTitle,
              product_id: productId,
            });
          }
        }
      }

      // Method 2: Look for product JSON data in script tags (most comprehensive)
      const scripts = doc.querySelectorAll('script[type="application/json"]');
      for (const script of scripts) {
        try {
          const data = JSON.parse(script.textContent);
          
          // Check if this is product data with variants array
          if (data.variants && Array.isArray(data.variants) && data.variants.length > 0) {
            // Clear existing variants and use this data (most reliable)
            this.#variants = [];
            for (const variant of data.variants) {
              if (variant.id && variant.title) {
                this.#variants.push({
                  id: variant.id.toString(),
                  title: variant.title,
                  product_id: productId,
                });
              }
            }
            if (this.#variants.length > 0) break;
          }
        } catch (e) {
          // Continue searching
        }
      }
      
      // Method 2b: Look for product data in window.__INITIAL_STATE__ or data attributes
      if (this.#variants.length === 0) {
        // Try to find product data in data attributes
        const productElements = doc.querySelectorAll('[data-product-id="' + productId + '"]');
        for (const element of productElements) {
          const variantsData = element.getAttribute('data-variants');
          if (variantsData) {
            try {
              const variants = JSON.parse(variantsData);
              if (Array.isArray(variants)) {
                for (const variant of variants) {
                  if (variant.id && variant.title) {
                    this.#variants.push({
                      id: variant.id.toString(),
                      title: variant.title,
                      product_id: productId,
                    });
                  }
                }
                if (this.#variants.length > 0) break;
              }
            } catch (e) {
              // Continue
            }
          }
        }
      }

      // Method 3: Look for variant-picker with variant data
      if (this.#variants.length === 0) {
        const variantPickers = doc.querySelectorAll('variant-picker script[type="application/json"]');
        for (const script of variantPickers) {
          try {
            const variantData = JSON.parse(script.textContent);
            if (variantData.id && variantData.title) {
              this.#variants.push({
                id: variantData.id.toString(),
                title: variantData.title,
                product_id: productId,
              });
            }
          } catch (e) {
            // Continue searching
          }
        }
      }

      // Method 4: Try to find product JSON in window.__INITIAL_STATE__ or similar
      // This is a last resort - look for any script that might contain product data
      if (this.#variants.length === 0) {
        const allScripts = doc.querySelectorAll('script');
        for (const script of allScripts) {
          const content = script.textContent || '';
          // Look for patterns like "variants":[...] in script content
          if (content.includes('"variants"') || content.includes("'variants'")) {
            try {
              // Try to extract JSON from the script
              const jsonMatch = content.match(/\{[^}]*"variants"\s*:\s*\[[^\]]*\][^}]*\}/);
              if (jsonMatch) {
                const data = JSON.parse(jsonMatch[0]);
                if (data.variants && Array.isArray(data.variants)) {
                  for (const variant of data.variants) {
                    if (variant.id && variant.title) {
                      this.#variants.push({
                        id: variant.id.toString(),
                        title: variant.title,
                        product_id: productId,
                      });
                    }
                  }
                  if (this.#variants.length > 0) break;
                }
              }
            } catch (e) {
              // Continue searching
            }
          }
        }
      }

      // If still no variants found, check if product has only one variant (default variant)
      if (this.#variants.length === 0) {
        // Check for single variant product indicators
        const hasOnlyDefaultVariant = doc.querySelector('[data-has-only-default-variant="true"]');
        if (hasOnlyDefaultVariant) {
          this.#variants.push({
            id: productId, // Use product ID as variant ID for single variant products
            title: 'Default',
            product_id: productId,
          });
        }
      }

      this.#populateVariantSelect();
      this.refs.variantSelectorGroup.hidden = false;

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading product variants:', error);
    }
  };

  /**
   * Populate the variant select dropdown
   */
  #populateVariantSelect() {
    const select = this.refs.variantSelect;
    select.innerHTML = '<option value="">-- Select a variant --</option>';

    for (const variant of this.#variants) {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      select.appendChild(option);
    }
  }

  /**
   * Handle variant selection change
   * @param {Event} event
   */
  handleVariantChange = async (event) => {
    const variantSelect = /** @type {HTMLSelectElement} */ (event.target);
    const productSelect = this.refs.productSelect;
    const variantId = variantSelect.value;
    const productId = productSelect.value;

    if (!variantId || !productId) {
      this.refs.resultsContainer.hidden = true;
      return;
    }

    const selectedProductOption = productSelect.options[productSelect.selectedIndex];
    if (!selectedProductOption) return;
    const productHandle = selectedProductOption.dataset.handle || '';
    const productUrl = selectedProductOption.dataset.url || '';
    const productTitle = selectedProductOption.textContent || 'Product';

    this.refs.resultsContainer.hidden = true;

    this.#controller.abort();
    this.#controller = new AbortController();

    try {
      // Look for metafield data
      /** @type {{certificateOfAnalysis: string | null, ingredientTest: string | null, glutenTest: string | null, variants?: Array<any>}} */
      let testData = {
        certificateOfAnalysis: null,
        ingredientTest: null,
        glutenTest: null,
      };

      // Method 1: Try to get metafields from product JSON (if we have it cached)
      if (this.#productJsonData && variantId) {
        const selectedVariant = this.#productJsonData.variants?.find(
          (/** @type {any} */ v) => v.id.toString() === variantId.toString()
        );
        
        if (selectedVariant && selectedVariant.metafields) {
          // Check variant metafields
          const coaMetafield = selectedVariant.metafields.find(
            (/** @type {any} */ m) => m.namespace === 'custom' && m.key === 'certificate_of_analysis'
          );
          if (coaMetafield) {
            testData.certificateOfAnalysis = this.#normalizeUrl(coaMetafield.value);
          }
        }
      }

      // Method 2: Fetch product page to get metafield data from snippet
      if (!testData.certificateOfAnalysis) {
        const response = await fetch(productUrl || `/products/${productHandle}`, {
          signal: this.#controller.signal,
        });

        if (!response.ok) throw new Error('Failed to fetch product');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Try to find metafield data in script tags (from third-party-tests-metafields.liquid snippet)
        const metafieldScripts = doc.querySelectorAll('script[type="application/json"][data-metafields]');
        console.log('Found metafield scripts:', metafieldScripts.length, 'for product:', productId);
        console.log('Looking for variantId:', variantId, 'type:', typeof variantId);
        
        for (const script of metafieldScripts) {
          try {
            const scriptProductId = script.getAttribute('data-product-id');
            console.log('Checking script with product ID:', scriptProductId, 'vs', productId.toString());
            
            if (scriptProductId === productId.toString()) {
              console.log('Product ID matches! Parsing JSON...');
              console.log('Script textContent length:', script.textContent?.length);
              console.log('Script textContent preview:', script.textContent?.substring(0, 500));
              
              let data;
              try {
                // Decode HTML entities before parsing JSON
                const textContent = script.textContent || '';
                const decodedContent = textContent
                  .replace(/&quot;/g, '"')
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>');
                
                data = JSON.parse(decodedContent);
                console.log('Parsed metafield data:', data);
                console.log('Data variants:', data.variants);
                console.log('Data variants type:', typeof data.variants, 'isArray:', Array.isArray(data.variants));
                console.log('Looking for variant:', variantId, 'in variants:', data.variants);
              } catch (parseError) {
                console.error('JSON Parse Error:', parseError);
                console.error('Failed to parse:', script.textContent);
                throw parseError;
              }

              // Priority: Check variant-level metafields first (if variant is selected)
              if (data.variants && Array.isArray(data.variants) && data.variants.length > 0 && variantId) {
                console.log('Checking variants. Looking for variant ID:', variantId, 'Type:', typeof variantId);
                console.log('Available variant IDs:', data.variants.map((/** @type {any} */ v) => ({ id: v.id, type: typeof v.id, title: v.title })));
                
                // Find the selected variant - try both string and number comparison
                const variantIdStr = variantId.toString();
                const variantIdNum = parseInt(variantIdStr, 10);
                
                const selectedVariant = data.variants.find((/** @type {any} */ v) => {
                  const vIdStr = v.id?.toString() || '';
                  const vIdNum = typeof v.id === 'number' ? v.id : parseInt(vIdStr, 10);
                  
                  // Try multiple comparison methods
                  const matches = 
                    vIdStr === variantIdStr || 
                    vIdStr === variantId ||
                    v.id === variantId || 
                    v.id === variantIdNum ||
                    vIdNum === variantIdNum ||
                    (!isNaN(vIdNum) && !isNaN(variantIdNum) && vIdNum === variantIdNum);
                  
                  if (matches) {
                    console.log('Found matching variant:', v, 'Matched ID:', v.id);
                  }
                  return matches;
                });
                
                if (selectedVariant) {
                  console.log('Selected variant data:', selectedVariant);
                  console.log('Variant COA value:', selectedVariant.certificate_of_analysis);
                  
                  // Use variant-level metafields if they exist
                  if (selectedVariant.certificate_of_analysis && selectedVariant.certificate_of_analysis !== 'null' && selectedVariant.certificate_of_analysis !== null) {
                    testData.certificateOfAnalysis = this.#normalizeUrl(selectedVariant.certificate_of_analysis);
                    console.log('Found COA for variant:', testData.certificateOfAnalysis);
                  } else {
                    console.log('Variant found but no COA. certificate_of_analysis value:', selectedVariant.certificate_of_analysis);
                  }
                  if (selectedVariant.ingredient_test && selectedVariant.ingredient_test !== 'null' && selectedVariant.ingredient_test !== null) {
                    testData.ingredientTest = this.#normalizeUrl(selectedVariant.ingredient_test);
                  }
                  if (selectedVariant.gluten_test && selectedVariant.gluten_test !== 'null' && selectedVariant.gluten_test !== null) {
                    testData.glutenTest = this.#normalizeUrl(selectedVariant.gluten_test);
                  }
                } else {
                  console.log('No matching variant found. Searched for:', variantId, 'Available IDs:', data.variants.map((/** @type {any} */ v) => v.id));
                }
              } else {
                console.log('No variants in data or no variantId. variants:', data.variants, 'variantId:', variantId);
              }

              // Fallback: Check product-level metafields if variant-level ones don't exist
              if (!testData.certificateOfAnalysis && data.certificate_of_analysis) {
                testData.certificateOfAnalysis = this.#normalizeUrl(data.certificate_of_analysis);
              }
              if (!testData.ingredientTest && data.ingredient_test) {
                testData.ingredientTest = this.#normalizeUrl(data.ingredient_test);
              }
              if (!testData.glutenTest && data.gluten_test) {
                testData.glutenTest = this.#normalizeUrl(data.gluten_test);
              }

              console.log('Final testData after processing:', testData);
              break;
            } else {
              console.log('Product ID does not match. Script product ID:', scriptProductId, 'vs', productId.toString());
            }
          } catch (e) {
            console.error('Error parsing metafield script:', e);
            console.error('Script content preview:', script.textContent?.substring(0, 200));
            // Continue searching
          }
        }
        
        console.log('Finished checking all metafield scripts. Final testData:', testData);
      }

      // Method 3: Try fetching product JSON again to get metafields
      if (!testData.certificateOfAnalysis && variantId) {
        try {
          const jsonUrl = `/products/${productHandle}.json`;
          const jsonResponse = await fetch(jsonUrl, {
            signal: this.#controller.signal,
          });

          if (jsonResponse.ok) {
            const productData = await jsonResponse.json();
            const selectedVariant = productData.product?.variants?.find(
              (/** @type {any} */ v) => v.id.toString() === variantId.toString()
            );
            
            // Note: Product JSON endpoint typically doesn't include metafields
            // But we can try to access them if they're there
            if (selectedVariant && selectedVariant.metafields) {
              const coaMetafield = selectedVariant.metafields.find(
                (/** @type {any} */ m) => m.namespace === 'custom' && m.key === 'certificate_of_analysis'
              );
              if (coaMetafield) {
                testData.certificateOfAnalysis = this.#normalizeUrl(coaMetafield.value);
              }
            }
          }
        } catch (e) {
          // Continue
        }
      }

      // Display results
      const selectedVariantOption = variantSelect.options[variantSelect.selectedIndex];
      this.#displayProductResults(
        {
          id: productId,
          name: productTitle,
          url: productUrl,
          handle: productHandle,
        },
        testData,
        selectedVariantOption?.textContent || ''
      );

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error fetching product test data:', error);
      this.#displayProductResults(
        {
          id: productId,
          name: productTitle,
          url: productUrl,
          handle: productHandle,
        },
        {
          certificateOfAnalysis: null,
          ingredientTest: null,
          glutenTest: null,
        },
        variantSelect.options[variantSelect.selectedIndex]?.textContent || ''
      );
    }
  };

  /**
   * Normalize URL - handle both relative and absolute URLs, and file references
   * @param {string | null | undefined | {url: string}} url
   * @returns {string | null}
   */
  #normalizeUrl(url) {
    if (!url) return null;
    if (typeof url === 'string') {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      if (url.startsWith('//')) {
        return 'https:' + url;
      }
      if (url.startsWith('/')) {
        return new URL(url, location.origin).toString();
      }
      return url;
    }
    if (typeof url === 'object' && url !== null && 'url' in url) {
      const urlObj = /** @type {{url: string}} */ (url);
      if (typeof urlObj.url === 'string') {
        return urlObj.url;
      }
    }
    return null;
  }

  /**
   * Display product results
   * @param {{id: string, name: string, url: string, handle: string}} product
   * @param {{certificateOfAnalysis: string | null, ingredientTest: string | null, glutenTest: string | null, variants?: Array<any>}} testData
   * @param {string} variantTitle
   */
  #displayProductResults(product, testData, variantTitle) {
    this.refs.resultsContainer.hidden = false;
    this.refs.noResultsSection.hidden = true;

    this.refs.productResults.innerHTML = this.#renderProductCard(product, testData, variantTitle);
  }

  /**
   * Render product card HTML
   * @param {{id: string, name: string, url: string, handle: string}} product
   * @param {{certificateOfAnalysis: string | null, ingredientTest: string | null, glutenTest: string | null, variants?: Array<any>}} testData
   * @param {string} variantTitle
   */
  #renderProductCard(product, testData, variantTitle) {
    const tests = [];
    if (testData.certificateOfAnalysis) {
      tests.push({ 
        name: 'Certificate of Analysis', 
        url: testData.certificateOfAnalysis,
        type: 'coa'
      });
    }
    if (testData.ingredientTest) {
      tests.push({ 
        name: 'Ingredient Test', 
        url: testData.ingredientTest,
        type: 'ingredient'
      });
    }
    if (testData.glutenTest) {
      tests.push({ 
        name: 'Gluten Test', 
        url: testData.glutenTest,
        type: 'gluten'
      });
    }

    let testLinksHtml = '';
    if (tests.length > 0) {
      testLinksHtml = `
        <div class="third-party-tests__test-links">
          ${tests.map((test) => {
            return `
              <a href="${test.url}" target="_blank" rel="noopener noreferrer" class="third-party-tests__test-link">
                <span class="third-party-tests__test-link-text">${test.name}</span>
                <span class="third-party-tests__test-link-icon">
                  ${this.#getExternalIcon()}
                </span>
              </a>
            `;
          }).join('')}
        </div>
      `;
    } else {
      testLinksHtml = `
        <p class="third-party-tests__no-tests-message">
          Test documents are being processed. Please check back soon.
        </p>
      `;
    }

    const variantDisplay = variantTitle && variantTitle !== 'Default' ? ` - ${this.#escapeHtml(variantTitle)}` : '';

    return `
      <div class="third-party-tests__product-card">
        <h3 class="third-party-tests__product-name">${this.#escapeHtml(product.name)}${variantDisplay}</h3>
        ${testLinksHtml}
      </div>
    `;
  }

  /**
   * Get external link icon SVG
   */
  #getExternalIcon() {
    return `
      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3.33333H8V4.66667H10.3907L6.19533 8.862L7.138 9.80467L11.3333 5.60933V8H12.6667V3.33333H12Z" fill="currentColor"/>
        <path d="M3.33333 4.66667H6.66667V3.33333H3.33333C2.59667 3.33333 2 3.93 2 4.66667V12.6667C2 13.4033 2.59667 14 3.33333 14H11.3333C12.07 14 12.6667 13.4033 12.6667 12.6667V9.33333H11.3333V12.6667H3.33333V4.66667Z" fill="currentColor"/>
      </svg>
    `;
  }

  /**
   * View PDF in modal
   * @param {Event} event
   */
  viewPdf = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const pdfUrl = button.getAttribute('data-pdf-url');
    const testName = button.getAttribute('data-test-name') || 'Certificate of Analysis';

    if (!pdfUrl || !this.refs.pdfObject || !this.refs.pdfDialog) {
      console.error('Missing required refs for PDF viewer');
      return;
    }

    // Try using object tag first (better for PDFs)
    if (this.refs.pdfObject) {
      this.refs.pdfObject.data = pdfUrl;
    }
    
    // Fallback: Use Google Docs Viewer in iframe if object doesn't work
    if (this.refs.pdfIframe) {
      const encodedUrl = encodeURIComponent(pdfUrl);
      const viewerUrl = `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
      this.refs.pdfIframe.src = viewerUrl;
    }
    
    // Update dialog title
    if (this.refs.pdfDialogTitle) {
      this.refs.pdfDialogTitle.textContent = testName;
    }

    // Show the modal using native dialog API
    this.refs.pdfDialog.showModal();
  };

  /**
   * Close PDF viewer
   * @param {Event} event
   */
  closePdfViewer = (event) => {
    event.preventDefault();
    
    if (this.refs.pdfDialog) {
      this.refs.pdfDialog.close();
    }
    
    // Clear PDF data when closing
    if (this.refs.pdfObject) {
      this.refs.pdfObject.data = '';
    }
    if (this.refs.pdfIframe) {
      this.refs.pdfIframe.src = '';
    }
  };

  /**
   * Escape HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('third-party-tests-component')) {
  customElements.define('third-party-tests-component', ThirdPartyTestsComponent);
}
